/**
 * An ⓘ beside a label, explaining what the control under it does.
 *
 * A tooltip, so a mouse gets it on hover and a keyboard on focus — but also
 * opened on click, because a tooltip that only opens on hover is unreachable
 * on a tablet, which is where site staff read the schedule. Touch taps toggle
 * the hint; mouse clicks keep a hover-opened hint visible. A tap anywhere else,
 * or Escape, dismisses it.
 */
import { useRef, useState } from "react";
import { Info } from "lucide-react";

import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { fill } from "@/lib/i18n/fill";
import { useLanguage } from "@/lib/i18n/LanguageContext";

export function InfoHint({ label, children }: { label: string; children: React.ReactNode }) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const touchPointer = useRef(false);

  return (
    <Tooltip open={open} onOpenChange={setOpen} delayDuration={150}>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={fill(t.pm.issues.dependencies.infoLabel, { label })}
          onPointerDown={(e) => {
            e.preventDefault();
            touchPointer.current = e.pointerType === "touch";
          }}
          onPointerUp={(e) => {
            // WebKit may omit the compatibility click when touch pointer-down is prevented.
            if (e.pointerType === "touch") setOpen((wasOpen) => !wasOpen);
          }}
          onPointerCancel={() => {
            touchPointer.current = false;
          }}
          onClick={(e) => {
            e.preventDefault();
            if (!touchPointer.current || e.detail === 0) setOpen(true);
            touchPointer.current = false;
          }}
          className="inline-flex shrink-0 items-center rounded-full text-primary/70 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          <Info className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs space-y-1.5 text-xs leading-snug">
        {children}
      </TooltipContent>
    </Tooltip>
  );
}
